import React, { FC, useEffect, useRef, useState } from "react";
import blank from "../../images/blank.svg";
import { ReactComponent as Loading } from "../../images/loading.svg";
import "./Canvas.component.scss";
import { filter7, randomGreyHex } from "../../utils/helpers";
const LAST_STEP = 6;

type Props = {
  image: string;
  filterType: number;
  filterStep: number;
  height: number;
  width: number;
  isGameEnded: boolean;
};

export const Canvas: FC<Props> = (props) => {
  const { image, filterType, filterStep, height, width, isGameEnded } = props;
  const [loading, setLoading] = useState(true);

  const filtersDir = require.context("../../images/filters/", true, /\.svg$/);
  const imageDir = require.context("../../images/logo/", true);
  const canvas = useRef<HTMLCanvasElement>(null);
  const { filter7Steps } = filter7();

  const fill = (
    fillStyle: string | CanvasGradient | CanvasPattern,
    xCor: number,
    yCor: number,
    cellWidth: number,
    cellHeight: number,
    context: CanvasRenderingContext2D
  ) => {
    context.fillStyle = fillStyle;
    context.fillRect(xCor, yCor, cellWidth, cellHeight);
  };  

  const drawCanvas = (
    filter: HTMLImageElement,
    logo: HTMLImageElement,
    context: CanvasRenderingContext2D,
    filterStep: number
  ) => {
    if (filter.complete && logo.complete) {
      context.clearRect(0, 0, width, height);
      const wRatio = width / logo.width;
      const hRatio = height / logo.height;
      const ratio = Math.min(wRatio, hRatio);
      const centerShift_x = (width - logo.width * ratio) / 2;
      const centerShift_y = (height - logo.height * ratio) / 2;

      context.drawImage(
        logo,
        0,
        0,
        logo.width,
        logo.height,
        centerShift_x,
        centerShift_y,
        logo.width * ratio,
        logo.height * ratio
      );

      if (filterType === 7) {
        const cellsCount = 10;
        const cellWidth = ~~(width / cellsCount);
        const cellHeight = ~~(height / cellsCount);
        const step = isGameEnded
          ? (`step${LAST_STEP}` as keyof typeof filter7Steps)
          : (`step${filterStep}` as keyof typeof filter7Steps);
        const filter = filter7Steps[step];

        let xCor = 0;
        let yCor = 0;
        let index = 0;

        for (let i = 0; i < 10; i++) {
          yCor = yCor + cellHeight;
          if (i === 0) yCor = 0;
          for (let j = 0; j < 10; j++) {
            xCor = xCor + cellWidth;
            if (j === 0) xCor = 0;
            if (filter[index] !== 0)
              fill(randomGreyHex(xCor, yCor), xCor, yCor, cellWidth, cellHeight, context);
            index++;
            if (j === 9) xCor = 0;
          }
        }
      } else {
        context.drawImage(filter, 0, 0, width, height);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    const context = canvas.current?.getContext("2d");
    setLoading(true);
    const logo = new Image();
    const filter = new Image();

    if (context) {
      logo.onload = () => {
        drawCanvas(filter, logo, context, filterStep);
      };
      filter.onload = () => {
        drawCanvas(filter, logo, context, filterStep);
      };
    }
    // src must after onload for correct first load
    logo.src = imageDir(`./${image}`);
    if (filterType !== 7) {
      filter.src =
        filterStep === LAST_STEP || isGameEnded
          ? (filter.src = blank)
          : filtersDir(`./filter-${filterType}/step-${filterStep}.svg`);
    }

    return context?.clearRect(0, 0, width, height);
  }, [image, filterType, filterStep, height, width]);

  return (
    <div className="container">
      {loading ? <Loading className="loading" /> : null}
      <canvas ref={canvas} width={width} height={height} />
    </div>
  );
};

export default Canvas;
